import { httpService } from '../../../utility/httpService';
import { httpConstants } from '../../../constants';
import { API_GRAPH_BASE, API_BASE } from '../../../config/env';
import { ORCHESTRATOR_CONFIG } from '../../../config/orchestratorConfig';
import type {
  NewSolution,
  ApprovalState,
  KeyInformationData,
} from '../shared/types/solution-xplr.interface';
import type { AttachmentItemProps } from '../../../common/components/AttachmentUpload/AttachmentUpload';

export interface BulkDataAssetsRequest {
  additional_data_sources: unknown[];
  current_data_sources: unknown[];
  solution_id: string;
}

export interface BulkDataAssetsResponse {
  success?: boolean;
  [key: string]: unknown;
}

export async function canCreateSolution(organizationId: string): Promise<boolean> {
  const url = `${API_GRAPH_BASE}/organization/${organizationId}/solution-limit-check`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await httpService<{ can_create_solution?: boolean }>(
    httpConstants.METHOD_TYPE.GET,
    url,
    headers
  );

  if (response?.can_create_solution === false) {
    return false;
  }

  return true;
}

export async function deleteNewSolution(solutionId: string): Promise<void> {
  const url = `${API_GRAPH_BASE}/new-solution/${solutionId}`;
  await httpService<unknown>(httpConstants.METHOD_TYPE.DELETE, url);
}

export async function duplicateNewSolution(
  solutionId: string,
  organizationId: string
): Promise<NewSolution> {
  const url = `${API_GRAPH_BASE}/new-solution/${solutionId}/duplicate`;

  const response = await httpService<NewSolution>(httpConstants.METHOD_TYPE.POST, url, {
    organization_id: organizationId,
  });

  return response;
}

export async function bulkCreateDataAssets(
  payload: BulkDataAssetsRequest
): Promise<BulkDataAssetsResponse> {
  const url = `${API_GRAPH_BASE}/data-assets/bulk`;

  const response = await httpService<BulkDataAssetsResponse>(
    httpConstants.METHOD_TYPE.POST,
    url,
    payload
  );

  return response;
}

export async function updateSolutionApprovalState(
  solutionId: string,
  approvalState: ApprovalState
): Promise<void> {
  const url = `${API_GRAPH_BASE}/new-solution/${solutionId}`;

  await httpService<unknown>(httpConstants.METHOD_TYPE.PATCH, url, {
    approval_state: approvalState,
  });
}

export interface MarkdownWebhookRequest {
  orchestratorUrl: string;
  solutionId: string;
  organizationId: string;
  organizationName: string;
  label: string;
}

export async function triggerMarkdownWebhook(request: MarkdownWebhookRequest): Promise<unknown> {
  const { orchestratorUrl, solutionId, organizationId, organizationName, label } = request;

  const payload = {
    solution_id: solutionId,
    organization_id: organizationId,
    organization_name: organizationName,
    url: `${API_GRAPH_BASE}/webhook/markdown`,
    label,
  };

  const response = await httpService<unknown>(
    httpConstants.METHOD_TYPE.POST,
    orchestratorUrl,
    payload,
    undefined,
    true
  );

  return response;
}

export interface EvaluateSolutionRequest {
  url: string;
  keyInformationData: KeyInformationData;
  label: string;
  organizationId: string;
  documents: AttachmentItemProps[];
}

export async function evaluateSolution(
  request: EvaluateSolutionRequest
): Promise<unknown> {
  const { url, keyInformationData, label, organizationId, documents } = request;

  const headers: Record<string, string> = {};

  const payload = {
    ...keyInformationData,
    label,
    organization_id: organizationId,
    documents: documents.map((file) => file.url),
  };

  const response = await httpService<unknown>(
    httpConstants.METHOD_TYPE.POST,
    url,
    payload,
    headers,
    true
  );

  return response;
}

export interface UploadFilesResponse {
  files: string[];
}

export async function uploadFilesToNexus(formData: FormData): Promise<UploadFilesResponse> {
  const url = `${API_BASE}/api/nexus/files`;

  const response = await httpService<UploadFilesResponse>(
    httpConstants.METHOD_TYPE.POST,
    url,
    formData
  );

  return response;
}


export interface CreateDataSourceRequest {
  documents: any[];
  input: string;
  label: string;
  organization_id: string;
  organization_name: string;
  user_email: string;
}

export async function createDataSource(request: CreateDataSourceRequest): Promise<any> {
  const url = ORCHESTRATOR_CONFIG.ENDPOINTS.CREATE_DATA_SOURCE;
  const response = await httpService<any>(
    httpConstants.METHOD_TYPE.POST,
    url,
    request,
    undefined,
    true // isOrchestrator
  );
  return response;
}

export interface HandleNextRequest {
  solution_id: string;
  organization_id: string;
  current_data_sources: any[];
  key_information_data: KeyInformationData;
  env_type: string;
}

export async function triggerHandleNext(request: HandleNextRequest): Promise<any> {
  const url = ORCHESTRATOR_CONFIG.ENDPOINTS.HANDLE_NEXT;
  const response = await httpService<any>(
    httpConstants.METHOD_TYPE.POST,
    url,
    request,
    undefined,
    true
  );
  return response;
}
